// Thin fetch layer against the Mail BFF. Mirror types are kept deliberately
// separate from the BFF's C# contracts — both sides describe the wire format
// independently so a change on one side surfaces as a PR diff on the other.

const baseUrl = import.meta.env.VITE_MAIL_BFF_BASE_URL;

if (!baseUrl) {
    throw new Error("VITE_MAIL_BFF_BASE_URL is required.");
}

/** Per-file attachment ceiling — mirrors AttachmentEndpoints.MaxBytes (25 MB,
 *  Gmail's limit). Bigger local files are offered as Drive links instead. */
export const MAIL_ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;

export interface MailAddress {
    name: string;
    email: string;
}

export interface Folder {
    id: string;
    name: string;
    kind: string;
    unreadCount: number;
    isSystem: boolean;
    /** Total messages in the folder — meaningful for Drafts/Sent/etc.
     *  where unreadCount is always zero. */
    totalCount: number;
    /** Parent folder id for nested folders (e.g. Customer → Balaji Dairy);
     *  null/undefined for top-level. */
    parentId?: string | null;
    /** 0 for top-level; used for tree indentation. */
    depth?: number;
}

export type MessageCategory =
    | "work"
    | "personal"
    | "travel"
    | "finance"
    | "urgent"
    | "followup"
    | "meetings"
    | "social"
    | "reference"
    | "promotions"
    | "updates";

export const allMessageCategories: MessageCategory[] = [
    "work",
    "personal",
    "travel",
    "finance",
    "urgent",
    "followup",
    "meetings",
    "social",
    "reference",
    "promotions",
    "updates"
];

/** One page of a folder, newest first, with the folder's real total. */
export interface MessagePage {
    items: MessageSummary[];
    total: number;
    skip: number;
    take: number;
}

export interface MessageSummary {
    id: string;
    folderId: string;
    from: MailAddress;
    subject: string;
    preview: string;
    receivedAt: string;
    isRead: boolean;
    isFlagged: boolean;
    hasAttachments: boolean;
    category: MessageCategory | null;
    /** Sender's domain is NOT in the tenant's own-domain allow-list.
     *  Reading pane renders a yellow warning banner; the message list
     *  renders a small "Ext" chip next to the sender. Computed by the
     *  BFF (IExternalSenderClassifier) so the UI never has to mirror
     *  the rule. Default false when the BFF can't classify (no config). */
    isExternal?: boolean;
    /** Outlook-style pin to top. Pinned rows render above the rest of
     *  the folder, ordered by pinnedAt desc. */
    isPinned?: boolean;
    /** The first few attachments (IMAP list) — the chips under a row — and how many there are in all. */
    attachmentChips?: AttachmentChip[] | null;
    attachmentCount?: number;
}

export interface AttachmentChip {
    /** MIME part — the attachment id for /api/messages/{id}/attachments/{part}. */
    id?: string;
    name: string;
    contentType: string;
    sizeBytes: number;
}

export interface MessageAttachment {
    id: string;
    fileName: string;
    contentType: string;
    sizeBytes: number;
    isInline: boolean;
    contentId: string | null;
}

export interface Message extends MessageSummary {
    to: MailAddress[];
    /** Carbon copies; absent from stores that do not keep them. */
    cc?: MailAddress[] | null;
    bodyHtml: string;
    attachments: MessageAttachment[];
    /** RFC 3798: when the inbound message carried a
     *  Disposition-Notification-To header, the value lands here so the
     *  reading pane can prompt the user to send a Message Disposition
     *  Notification reply. Null on messages without the header. */
    receiptRequestedTo?: string | null;
    /** True once the user has either sent the MDN or dismissed the
     *  prompt. Stops the reading pane re-asking on every re-open. */
    receiptResolved?: boolean;
    /** Outlook-style pin (mirrored from MessageSummary).*/
    isPinned?: boolean;
    /** The whole header block (mail-server mode only), for "View message details". */
    rawHeaders?: string | null;
    /** RFC 2369 List-Unsubscribe value, and whether the sender allows RFC 8058 one-click. */
    listUnsubscribe?: string | null;
    listUnsubscribeOneClick?: boolean;
    /** SPF / DKIM / DMARC as our server recorded them on arrival. */
    authenticationResults?: string | null;
}

export interface UnsubscribeResult {
    /** "one-click" (done for you), "link" (a page to open), "mailto" (an email to send) or "none". */
    method: "one-click" | "link" | "mailto" | "none";
    ok: boolean;
    url: string | null;
    mailTo: string | null;
    message: string;
}

export interface TaskItem {
    id: string;
    title: string;
    notes: string | null;
    /** ISO 8601 string or null. */
    dueAt: string | null;
    isCompleted: boolean;
    completedAt: string | null;
    createdAt: string;
    updatedAt: string;
    /** When the task was created from a message (reading-pane action),
     *  the original message's short id. UI deep-links back to /mail
     *  with this selected. Stays through completion/edits. */
    relatedMessageId: string | null;
    /** User list id; null = the default "Tasks" list. */
    listId: string | null;
    isImportant: boolean;
    /** ISO 8601 — when the reminder pops. */
    reminderAt: string | null;
    /** RRULE subset ("FREQ=WEEKLY;BYDAY=MO,WE"); null = does not repeat. */
    recurrence: string | null;
    /** "YYYY-MM-DD" in the user's zone; in My Day only while it is today. */
    myDayDate: string | null;
    steps: TaskStep[];
    /** "flagged" = mirrors a flagged message (Flagged email list). */
    source: "manual" | "flagged";
}

export interface TaskStep {
    id: string;
    title: string;
    isCompleted: boolean;
}

export interface UpsertTaskPayload {
    title: string;
    notes?: string | null;
    dueAt?: string | null;
    reminderAt?: string | null;
    recurrence?: string | null;
    isImportant?: boolean;
    myDayDate?: string | null;
    listId?: string | null;
    steps?: TaskStep[];
    relatedMessageId?: string | null;
}

export interface CompleteTaskResponse {
    task: TaskItem;
    /** The next occurrence a repeating task created. */
    next: TaskItem | null;
}

export interface TaskList {
    id: string;
    name: string;
    emoji: string | null;
    /** "#RRGGBB" */
    color: string | null;
    sortOrder: number;
}

export interface UpsertTaskListPayload {
    name: string;
    emoji?: string | null;
    color?: string | null;
    sortOrder?: number | null;
}

export interface FlaggedSyncResult {
    created: number;
    completed: number;
    reopened: number;
    partial: boolean;
}

export interface SendMessagePayload {
    to: string;
    cc?: string;
    bcc?: string;
    subject: string;
    bodyHtml: string;
    attachmentIds?: string[];
    /** Sets `Disposition-Notification-To` on the outgoing message
     *  (RFC 3798). MDN-aware clients prompt the recipient to send
     *  a read receipt. Default off. */
    requestReadReceipt?: boolean;
}

export interface SendMessageResponse {
    id: string;
    status: string;
}

export interface CreateMeetingPayload {
    subject: string;
    /** ISO-8601 with offset, e.g. "2026-04-30T10:30:00.000Z". */
    startUtc: string;
    /** ISO-8601 with offset. */
    endUtc: string;
}

export interface CreateMeetingResponse {
    provider: string;
    joinUrl: string;
    providerMeetingId: string;
    conferenceId: string | null;
    dialInNumbers: string[];
    subject: string;
    startUtc: string;
    endUtc: string;
}

export interface AttachmentUpload {
    id: string;
    fileName: string;
    contentType: string;
    sizeBytes: number;
}

export interface MailIdentity {
    id: string;
    displayName: string;
    emailAddress: string;
    replyTo: string | null;
    organisation: string | null;
    signatureHtml: string | null;
    isDefault: boolean;
    sortOrder: number;
}

export interface UpsertMailIdentityPayload {
    displayName: string;
    emailAddress: string;
    replyTo?: string | null;
    organisation?: string | null;
    signatureHtml?: string | null;
    isDefault: boolean;
    sortOrder: number;
}

export type DraftKind = "New" | "Reply" | "ReplyAll" | "Forward";

export interface DraftDto {
    id: string;
    identityId: string | null;
    inReplyToMessageId: string | null;
    kind: DraftKind;
    to: string;
    cc: string;
    bcc: string;
    subject: string;
    bodyHtml: string;
    attachmentIds: string[];
    updatedAt: string;
}

export interface UpsertDraftPayload {
    identityId?: string | null;
    inReplyToMessageId?: string | null;
    kind: DraftKind;
    to: string;
    cc: string;
    bcc: string;
    subject: string;
    bodyHtml: string;
    attachmentIds: string[];
}

// Labels / tags
export interface Label {
    id: string;
    name: string;
    color: string;
    sortOrder: number;
}

export interface UpsertLabelPayload {
    name: string;
    color: string;
    sortOrder: number;
}

// Filters (Sieve-lite)
export interface FilterRow {
    id: string;
    name: string;
    isEnabled: boolean;
    sortOrder: number;
    conditionsJson: string;
    actionsJson: string;
}

export interface UpsertFilterPayload {
    name: string;
    isEnabled: boolean;
    sortOrder: number;
    conditionsJson: string;
    actionsJson: string;
}

// Auto-responder rules (delay + match + template/AI)
export interface AutoResponderRow {
    id: string;
    name: string;
    isEnabled: boolean;
    sortOrder: number;
    matchAllMail: boolean;
    fromSenders: string;
    delaySeconds: number;
    mode: "template" | "ai";
    subject: string;
    bodyHtml: string;
    aiInstructions: string;
}

export type UpsertAutoResponderPayload = Omit<AutoResponderRow, "id">;

export interface ApplyFilterResult { matched: number; moved: number; }

// Vacation / auto-responder
export interface VacationResponder {
    isEnabled: boolean;
    subject: string;
    bodyHtml: string;
    startAt: string | null;
    endAt: string | null;
}

// Templates / canned responses
export interface Template {
    id: string;
    name: string;
    subject: string;
    bodyHtml: string;
}

export interface UpsertTemplatePayload {
    name: string;
    subject?: string;
    bodyHtml?: string;
}

// Contacts
export interface Contact {
    id: string;
    displayName: string;
    emailAddress: string;
    phone?: string | null;
    company?: string | null;
    jobTitle?: string | null;
    notes?: string | null;
    isFavorite: boolean;
    /** True for organisation-directory (platform) suggestions in your own domain. */
    isDirectory?: boolean;
}

export interface UpsertContactPayload {
    displayName: string;
    emailAddress: string;
    phone?: string | null;
    company?: string | null;
    jobTitle?: string | null;
    notes?: string | null;
    isFavorite?: boolean;
}

// Snooze
export interface Snooze {
    id: string;
    messageId: string;
    wakeAt: string;
}

async function fetchJson<T>(
    path: string,
    accessToken: string | undefined,
    init: RequestInit = {}
): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set("Accept", "application/json");
    if (init.body) {
        headers.set("Content-Type", "application/json");
    }
    if (accessToken) {
        headers.set("Authorization", `Bearer ${accessToken}`);
    }

    const response = await fetch(new URL(path, baseUrl).toString(), {
        ...init,
        headers,
        credentials: "omit"
    });

    if (!response.ok) {
        const body = await response.text().catch(() => response.statusText);
        throw MailApiError.from(path, response.status, body);
    }

    return (await response.json()) as T;
}

async function uploadAttachment(
    file: File,
    accessToken: string | undefined
): Promise<AttachmentUpload> {
    const form = new FormData();
    form.append("file", file);

    const headers = new Headers();
    headers.set("Accept", "application/json");
    if (accessToken) {
        headers.set("Authorization", `Bearer ${accessToken}`);
    }

    const response = await fetch(new URL("/api/attachments", baseUrl).toString(), {
        method: "POST",
        headers,
        body: form,
        credentials: "omit"
    });

    if (!response.ok) {
        // The BFF answers ProblemDetails for size / blocked type / quota /
        // virus — show its words rather than a bare status code.
        let reason = `${response.status} ${response.statusText}`;
        try {
            const p = (await response.json()) as { title?: string; detail?: string };
            reason = [p.title, p.detail].filter(Boolean).join(" — ") || reason;
        } catch {
            // not JSON — keep the status
        }
        throw new Error(`Upload failed: ${reason}`);
    }

    return (await response.json()) as AttachmentUpload;
}

async function downloadInboundAttachment(
    messageId: string,
    attachmentId: string,
    fileName: string,
    accessToken: string | undefined
): Promise<void> {
    const headers = new Headers();
    if (accessToken) {
        headers.set("Authorization", `Bearer ${accessToken}`);
    }
    const url = new URL(
        `/api/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}`,
        baseUrl
    ).toString();
    const response = await fetch(url, { headers, credentials: "omit" });
    if (!response.ok) {
        throw new Error(`Download failed → ${response.status} ${response.statusText}`);
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    try {
        const anchor = document.createElement("a");
        anchor.href = objectUrl;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
}

export const mailApi = {
    listFolders: (token: string | undefined) =>
        fetchJson<Folder[]>("/api/folders", token),
    listMessages: (folderId: string, token: string | undefined) =>
        fetchJson<MessageSummary[]>(`/api/folders/${encodeURIComponent(folderId)}/messages`, token),
    /** One page (1-based) of a folder with its real total — Gmail's "1–100 of 9,876". */
    listMessagesPage: (folderId: string, page: number, pageSize: number, token: string | undefined) =>
        fetchJson<MessagePage>(
            `/api/folders/${encodeURIComponent(folderId)}/messages?paged=true&take=${pageSize}&skip=${(Math.max(1, page) - 1) * pageSize}`,
            token),
    getMessage: (id: string, token: string | undefined) =>
        fetchJson<Message>(`/api/messages/${encodeURIComponent(id)}`, token),
    /** Ask the sender to stop (RFC 8058 one-click when offered); says what was done or what is left for the person to do. */
    unsubscribe: (id: string, token: string | undefined) =>
        fetchJson<UnsubscribeResult>(`/api/messages/${encodeURIComponent(id)}/unsubscribe`, token, { method: "POST" }),
    sendMessage: (payload: SendMessagePayload, token: string | undefined) =>
        fetchJson<SendMessageResponse>("/api/messages", token, {
            method: "POST",
            body: JSON.stringify(payload)
        }),
    createMeeting: (payload: CreateMeetingPayload, token: string | undefined) =>
        fetchJson<CreateMeetingResponse>("/api/meetings", token, {
            method: "POST",
            body: JSON.stringify(payload)
        }),
    listThreadByMessage: (messageId: string, token: string | undefined) =>
        fetchJson<MessageSummary[]>(
            `/api/threads/by-message/${encodeURIComponent(messageId)}`,
            token
        ),

    // Tasks
    listTasks: (token: string | undefined, completed?: boolean) =>
        fetchJson<TaskItem[]>(
            `/api/tasks${completed === undefined ? "" : `?completed=${completed}`}`,
            token
        ),
    createTask: (payload: UpsertTaskPayload, token: string | undefined) =>
        fetchJson<TaskItem>("/api/tasks", token, {
            method: "POST",
            body: JSON.stringify(payload)
        }),
    updateTask: (id: string, payload: UpsertTaskPayload, token: string | undefined) =>
        fetchJson<TaskItem>(`/api/tasks/${encodeURIComponent(id)}`, token, {
            method: "PUT",
            body: JSON.stringify(payload)
        }),
    completeTask: (id: string, token: string | undefined) =>
        fetchJson<CompleteTaskResponse>(`/api/tasks/${encodeURIComponent(id)}/complete`, token, { method: "POST" }),
    /** Mirror the mailbox's flagged messages into the Flagged email list. */
    syncFlaggedTasks: (token: string | undefined) =>
        fetchJson<FlaggedSyncResult>("/api/tasks/flagged/sync", token, { method: "POST" }),
    listTaskLists: (token: string | undefined) =>
        fetchJson<TaskList[]>("/api/tasks/lists", token),
    createTaskList: (payload: UpsertTaskListPayload, token: string | undefined) =>
        fetchJson<TaskList>("/api/tasks/lists", token, { method: "POST", body: JSON.stringify(payload) }),
    updateTaskList: (id: string, payload: UpsertTaskListPayload, token: string | undefined) =>
        fetchJson<TaskList>(`/api/tasks/lists/${encodeURIComponent(id)}`, token, {
            method: "PUT",
            body: JSON.stringify(payload)
        }),
    deleteTaskList: (id: string, token: string | undefined) =>
        requireNoContent(`/api/tasks/lists/${encodeURIComponent(id)}`, token, "DELETE"),
    reorderTaskLists: (ids: string[], token: string | undefined) =>
        fetchJson<TaskList[]>("/api/tasks/lists/reorder", token, { method: "POST", body: JSON.stringify({ ids }) }),
    uncompleteTask: (id: string, token: string | undefined) =>
        fetchJson<TaskItem>(`/api/tasks/${encodeURIComponent(id)}/uncomplete`, token, { method: "POST" }),
    deleteTask: async (id: string, token: string | undefined) => {
        const headers = new Headers();
        if (token) headers.set("Authorization", `Bearer ${token}`);
        const res = await fetch(new URL(`/api/tasks/${encodeURIComponent(id)}`, baseUrl).toString(), {
            method: "DELETE",
            headers,
            credentials: "include"
        });
        if (!res.ok) throw new Error(`deleteTask failed: ${res.status}`);
    },
    sendReadReceipt: async (messageId: string, token: string | undefined) => {
        const headers = new Headers();
        if (token) headers.set("Authorization", `Bearer ${token}`);
        const res = await fetch(
            new URL(`/api/messages/${encodeURIComponent(messageId)}/send-receipt`, baseUrl).toString(),
            { method: "POST", headers, credentials: "include" }
        );
        if (!res.ok) throw new Error(`send-receipt failed: ${res.status}`);
    },
    dismissReadReceipt: async (messageId: string, token: string | undefined) => {
        const headers = new Headers();
        if (token) headers.set("Authorization", `Bearer ${token}`);
        const res = await fetch(
            new URL(`/api/messages/${encodeURIComponent(messageId)}/dismiss-receipt`, baseUrl).toString(),
            { method: "POST", headers, credentials: "include" }
        );
        if (!res.ok) throw new Error(`dismiss-receipt failed: ${res.status}`);
    },
    listIdentities: (token: string | undefined) =>
        fetchJson<MailIdentity[]>("/api/identities", token),
    createIdentity: (payload: UpsertMailIdentityPayload, token: string | undefined) =>
        fetchJson<MailIdentity>("/api/identities", token, {
            method: "POST",
            body: JSON.stringify(payload)
        }),
    updateIdentity: (id: string, payload: UpsertMailIdentityPayload, token: string | undefined) =>
        fetchJson<MailIdentity>(`/api/identities/${encodeURIComponent(id)}`, token, {
            method: "PUT",
            body: JSON.stringify(payload)
        }),
    deleteIdentity: async (id: string, token: string | undefined) => {
        const headers = new Headers();
        if (token) headers.set("Authorization", `Bearer ${token}`);
        const res = await fetch(new URL(`/api/identities/${encodeURIComponent(id)}`, baseUrl).toString(), {
            method: "DELETE",
            headers
        });
        if (!res.ok) throw new Error(`Delete identity → ${res.status} ${res.statusText}`);
    },
    setDefaultIdentity: async (id: string, token: string | undefined) => {
        const headers = new Headers();
        if (token) headers.set("Authorization", `Bearer ${token}`);
        const res = await fetch(new URL(`/api/identities/${encodeURIComponent(id)}/default`, baseUrl).toString(), {
            method: "POST",
            headers
        });
        if (!res.ok) throw new Error(`Set default identity → ${res.status} ${res.statusText}`);
    },
    listDrafts: (token: string | undefined) =>
        fetchJson<DraftDto[]>("/api/drafts", token),
    getDraft: (id: string, token: string | undefined) =>
        fetchJson<DraftDto>(`/api/drafts/${encodeURIComponent(id)}`, token),
    createDraft: (payload: UpsertDraftPayload, token: string | undefined) =>
        fetchJson<DraftDto>("/api/drafts", token, {
            method: "POST",
            body: JSON.stringify(payload)
        }),
    updateDraft: (id: string, payload: UpsertDraftPayload, token: string | undefined) =>
        fetchJson<DraftDto>(`/api/drafts/${encodeURIComponent(id)}`, token, {
            method: "PUT",
            body: JSON.stringify(payload)
        }),
    deleteDraft: async (id: string, token: string | undefined) => {
        const headers = new Headers();
        if (token) headers.set("Authorization", `Bearer ${token}`);
        const res = await fetch(new URL(`/api/drafts/${encodeURIComponent(id)}`, baseUrl).toString(), {
            method: "DELETE",
            headers
        });
        if (!res.ok) throw new Error(`Delete draft → ${res.status} ${res.statusText}`);
    },
    setRead: async (id: string, read: boolean, token: string | undefined) => {
        const path = `/api/messages/${encodeURIComponent(id)}/${read ? "read" : "unread"}`;
        await requireNoContent(path, token, "POST");
    },
    setFlagged: async (id: string, flagged: boolean, token: string | undefined) => {
        const path = `/api/messages/${encodeURIComponent(id)}/${flagged ? "flag" : "unflag"}`;
        await requireNoContent(path, token, "POST");
    },
    setPinned: async (id: string, pinned: boolean, token: string | undefined) => {
        const path = `/api/messages/${encodeURIComponent(id)}/${pinned ? "pin" : "unpin"}`;
        await requireNoContent(path, token, "POST");
    },
    moveMessage: async (id: string, folderId: string, token: string | undefined) => {
        await requireNoContent(`/api/messages/${encodeURIComponent(id)}/move`, token, "POST", { folderId });
    },
    archiveMessage: async (id: string, token: string | undefined) => {
        await requireNoContent(`/api/messages/${encodeURIComponent(id)}/archive`, token, "POST");
    },
    trashMessage: async (id: string, token: string | undefined) => {
        await requireNoContent(`/api/messages/${encodeURIComponent(id)}/trash`, token, "POST");
    },
    deleteMessage: async (id: string, token: string | undefined) => {
        await requireNoContent(`/api/messages/${encodeURIComponent(id)}`, token, "DELETE");
    },
    bulkMessages: async (
        action: "read" | "unread" | "archive" | "trash" | "delete" | "move",
        ids: string[],
        token: string | undefined,
        folderId?: string
    ) => {
        const body = action === "move"
            ? { ids, folderId: folderId ?? "inbox" }
            : { ids };
        return fetchJson<{ updated: number; notFound: string[] }>(
            `/api/messages/bulk/${action}`,
            token,
            { method: "POST", body: JSON.stringify(body) }
        );
    },
    search: (params: SearchParams, token: string | undefined) => {
        const qs = new URLSearchParams();
        for (const [key, value] of Object.entries(params)) {
            if (value === undefined || value === null || value === "") continue;
            qs.set(key, String(value));
        }
        return fetchJson<SearchResult>(`/api/search?${qs.toString()}`, token);
    },
    /** Move that reports the message's new id (IMAP MOVE assigns a new UID) — enables Undo. */
    relocate: (id: string, folderId: string, token: string | undefined) =>
        fetchJson<RelocateResult>(`/api/messages/${encodeURIComponent(id)}/relocate`, token, {
            method: "POST",
            body: JSON.stringify({ folderId })
        }),
    /** Header-chain (Message-ID / References) thread key per message id. Empty on non-IMAP stores. */
    threadKeys: (ids: string[], token: string | undefined) =>
        fetchJson<Record<string, string>>("/api/threads/keys", token, {
            method: "POST",
            body: JSON.stringify({ ids })
        }),

    // Labels
    listLabels: (token: string | undefined) => fetchJson<Label[]>("/api/labels", token),
    createLabel: (payload: UpsertLabelPayload, token: string | undefined) =>
        fetchJson<Label>("/api/labels", token, { method: "POST", body: JSON.stringify(payload) }),
    updateLabel: (id: string, payload: UpsertLabelPayload, token: string | undefined) =>
        fetchJson<Label>(`/api/labels/${encodeURIComponent(id)}`, token, {
            method: "PUT",
            body: JSON.stringify(payload)
        }),
    deleteLabel: async (id: string, token: string | undefined) => {
        const headers = new Headers();
        if (token) headers.set("Authorization", `Bearer ${token}`);
        const res = await fetch(new URL(`/api/labels/${encodeURIComponent(id)}`, baseUrl).toString(), {
            method: "DELETE",
            headers
        });
        if (!res.ok) throw new Error(`Delete label → ${res.status} ${res.statusText}`);
    },
    listMessageLabels: (messageId: string, token: string | undefined) =>
        fetchJson<Label[]>(`/api/messages/${encodeURIComponent(messageId)}/labels`, token),
    applyMessageLabel: (messageId: string, labelId: string, token: string | undefined) =>
        requireNoContent(
            `/api/messages/${encodeURIComponent(messageId)}/labels/${encodeURIComponent(labelId)}`,
            token,
            "POST"
        ),
    unapplyMessageLabel: (messageId: string, labelId: string, token: string | undefined) =>
        requireNoContent(
            `/api/messages/${encodeURIComponent(messageId)}/labels/${encodeURIComponent(labelId)}`,
            token,
            "DELETE"
        ),

    // Filters
    listFilters: (token: string | undefined) => fetchJson<FilterRow[]>("/api/filters", token),
    createFilter: (payload: UpsertFilterPayload, token: string | undefined) =>
        fetchJson<FilterRow>("/api/filters", token, { method: "POST", body: JSON.stringify(payload) }),
    updateFilter: (id: string, payload: UpsertFilterPayload, token: string | undefined) =>
        fetchJson<FilterRow>(`/api/filters/${encodeURIComponent(id)}`, token, {
            method: "PUT",
            body: JSON.stringify(payload)
        }),
    deleteFilter: async (id: string, token: string | undefined) => {
        const headers = new Headers();
        if (token) headers.set("Authorization", `Bearer ${token}`);
        const res = await fetch(new URL(`/api/filters/${encodeURIComponent(id)}`, baseUrl).toString(), {
            method: "DELETE",
            headers
        });
        if (!res.ok) throw new Error(`Delete filter → ${res.status} ${res.statusText}`);
    },
    // Retroactively run a filter over existing inbox mail.
    applyFilter: (id: string, token: string | undefined) =>
        fetchJson<ApplyFilterResult>(`/api/filters/${encodeURIComponent(id)}/apply`, token, { method: "POST" }),

    // Auto-responders
    listAutoResponders: (token: string | undefined) =>
        fetchJson<AutoResponderRow[]>("/api/auto-responders", token),
    createAutoResponder: (payload: UpsertAutoResponderPayload, token: string | undefined) =>
        fetchJson<AutoResponderRow>("/api/auto-responders", token, {
            method: "POST",
            body: JSON.stringify(payload)
        }),
    updateAutoResponder: (id: string, payload: UpsertAutoResponderPayload, token: string | undefined) =>
        fetchJson<AutoResponderRow>(`/api/auto-responders/${encodeURIComponent(id)}`, token, {
            method: "PUT",
            body: JSON.stringify(payload)
        }),
    deleteAutoResponder: async (id: string, token: string | undefined) => {
        const headers = new Headers();
        if (token) headers.set("Authorization", `Bearer ${token}`);
        const res = await fetch(new URL(`/api/auto-responders/${encodeURIComponent(id)}`, baseUrl).toString(), {
            method: "DELETE",
            headers
        });
        if (!res.ok) throw new Error(`Delete auto-responder → ${res.status} ${res.statusText}`);
    },

    // Vacation
    getVacation: (token: string | undefined) => fetchJson<VacationResponder>("/api/vacation", token),
    putVacation: (payload: VacationResponder, token: string | undefined) =>
        fetchJson<VacationResponder>("/api/vacation", token, {
            method: "PUT",
            body: JSON.stringify(payload)
        }),

    // Templates
    listTemplates: (token: string | undefined) => fetchJson<Template[]>("/api/templates", token),
    createTemplate: (payload: UpsertTemplatePayload, token: string | undefined) =>
        fetchJson<Template>("/api/templates", token, { method: "POST", body: JSON.stringify(payload) }),
    updateTemplate: (id: string, payload: UpsertTemplatePayload, token: string | undefined) =>
        fetchJson<Template>(`/api/templates/${encodeURIComponent(id)}`, token, {
            method: "PUT",
            body: JSON.stringify(payload)
        }),
    deleteTemplate: async (id: string, token: string | undefined) => {
        const headers = new Headers();
        if (token) headers.set("Authorization", `Bearer ${token}`);
        const res = await fetch(new URL(`/api/templates/${encodeURIComponent(id)}`, baseUrl).toString(), {
            method: "DELETE",
            headers
        });
        if (!res.ok) throw new Error(`Delete template → ${res.status} ${res.statusText}`);
    },

    // Snooze
    snoozeMessage: (messageId: string, wakeAt: string, token: string | undefined) =>
        fetchJson<Snooze>("/api/snoozes", token, {
            method: "POST",
            body: JSON.stringify({ messageId, wakeAt })
        }),

    // Scheduled send — queue a message to land at sendAt; the BFF's
    // ScheduledSendWorker dispatches via IOutboundMailSender on the tick.
    scheduleSend: (
        payload: {
            to: string;
            cc?: string;
            bcc?: string;
            subject: string;
            bodyHtml: string;
            attachmentIds: string[];
            sendAt: string;
        },
        token: string | undefined
    ) =>
        fetchJson<{ id: string; status: string }>("/api/scheduled-sends", token, {
            method: "POST",
            body: JSON.stringify(payload)
        }),
    listSnoozes: (token: string | undefined) => fetchJson<Snooze[]>("/api/snoozes", token),
    cancelSnooze: async (id: string, token: string | undefined) => {
        const headers = new Headers();
        if (token) headers.set("Authorization", `Bearer ${token}`);
        const res = await fetch(new URL(`/api/snoozes/${encodeURIComponent(id)}`, baseUrl).toString(), {
            method: "DELETE",
            headers
        });
        if (!res.ok) throw new Error(`Cancel snooze → ${res.status} ${res.statusText}`);
    },

    uploadAttachment,
    downloadInboundAttachment,

    // Contacts
    listContacts: (token: string | undefined) =>
        fetchJson<Contact[]>("/api/contacts", token),
    autocompleteContacts: (q: string, token: string | undefined) =>
        fetchJson<Contact[]>(`/api/contacts/autocomplete?q=${encodeURIComponent(q)}&take=8`, token),
    createContact: (payload: UpsertContactPayload, token: string | undefined) =>
        fetchJson<Contact>("/api/contacts", token, {
            method: "POST",
            body: JSON.stringify(payload)
        }),
    updateContact: (id: string, payload: UpsertContactPayload, token: string | undefined) =>
        fetchJson<Contact>(`/api/contacts/${encodeURIComponent(id)}`, token, {
            method: "PUT",
            body: JSON.stringify(payload)
        }),
    deleteContact: async (id: string, token: string | undefined) => {
        const headers = new Headers();
        if (token) headers.set("Authorization", `Bearer ${token}`);
        const res = await fetch(new URL(`/api/contacts/${encodeURIComponent(id)}`, baseUrl).toString(), {
            method: "DELETE",
            headers
        });
        if (!res.ok) throw new Error(`Delete contact → ${res.status} ${res.statusText}`);
    },

    // Export contacts — triggers a browser download of the blob
    exportContacts: async (format: "csv" | "vcf", token: string | undefined): Promise<void> => {
        const headers = new Headers();
        if (token) headers.set("Authorization", `Bearer ${token}`);
        const url = new URL(`/api/contacts/export.${format}`, baseUrl).toString();
        const res = await fetch(url, { headers, credentials: "omit" });
        if (!res.ok) throw new Error(`Export contacts → ${res.status} ${res.statusText}`);
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        try {
            const anchor = document.createElement("a");
            anchor.href = objectUrl;
            anchor.download = `contacts.${format}`;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
        } finally {
            URL.revokeObjectURL(objectUrl);
        }
    },

    // Import contacts — multipart form upload
    importContacts: async (
        file: File,
        token: string | undefined
    ): Promise<{ imported: number; skipped: number }> => {
        const form = new FormData();
        form.append("file", file);
        const headers = new Headers();
        headers.set("Accept", "application/json");
        if (token) headers.set("Authorization", `Bearer ${token}`);
        const res = await fetch(new URL("/api/contacts/import", baseUrl).toString(), {
            method: "POST",
            headers,
            body: form,
            credentials: "omit"
        });
        if (!res.ok) throw new Error(`Import contacts → ${res.status} ${res.statusText}`);
        return (await res.json()) as { imported: number; skipped: number };
    },

    // AI Chat
    aiChat: (req: AiChatRequest, token: string | undefined) =>
        fetchJson<AiChatResponse>("/api/ai/chat", token, {
            method: "POST",
            body: JSON.stringify(req)
        }),

    // Free machine translation (no AI backend / API key needed).
    // Calendar settings — "events from email" (invites + bookings, added on the server).
    getCalendarSettings: (token: string | undefined) =>
        fetchJson<CalendarSettings>("/api/calendar/settings", token),
    /** Partial update — only the fields sent change. */
    putCalendarSettings: (settings: Partial<CalendarSettings>, token: string | undefined) =>
        fetchJson<CalendarSettings>("/api/calendar/settings", token, {
            method: "PUT",
            body: JSON.stringify(settings)
        }),

    // UI preferences saved per user (theme, accent, density, layout…) — see lib/preferences.ts.
    getPreferences: (token: string | undefined) =>
        fetchJson<PreferencesEnvelope>("/api/mail/preferences", token),
    /** Partial patch, merged on the server. 200 → saved; 409 → `body` is the server's current document. */
    putPreferences: async (
        prefs: unknown,
        version: number,
        token: string | undefined
    ): Promise<{ status: number; body: PreferencesEnvelope | null }> => {
        const headers = new Headers({
            Accept: "application/json",
            "Content-Type": "application/json",
            "If-Match": `"${version}"`
        });
        if (token) headers.set("Authorization", `Bearer ${token}`);
        const res = await fetch(new URL("/api/mail/preferences", baseUrl).toString(), {
            method: "PUT",
            headers,
            body: JSON.stringify({ prefs }),
            credentials: "omit",
            // Lets the save finish when it is sent as the page closes.
            keepalive: true
        });
        const body = res.status === 200 || res.status === 409
            ? ((await res.json().catch(() => null)) as PreferencesEnvelope | null)
            : null;
        return { status: res.status, body };
    },

    // Safe senders — remote content loads automatically only for these.
    listSafeSenders: (token: string | undefined) => fetchJson<SafeSender[]>("/api/safe-senders", token),
    addSafeSender: (address: string, token: string | undefined) =>
        fetchJson<SafeSender>("/api/safe-senders", token, {
            method: "POST",
            body: JSON.stringify({ address })
        }),
    removeSafeSender: (id: string, token: string | undefined) =>
        requireNoContent(`/api/safe-senders/${encodeURIComponent(id)}`, token, "DELETE"),

    // Translate an email's text pieces in order (layout-preserving Translate).
    translateSegments: (segments: string[], targetLang: string, token: string | undefined) =>
        fetchJson<TranslateSegmentsResponse>("/api/translate/segments", token, {
            method: "POST",
            body: JSON.stringify({ segments, targetLang })
        }),

    translate: (text: string, targetLang: string, token: string | undefined) =>
        fetchJson<TranslateResponse>("/api/translate", token, {
            method: "POST",
            body: JSON.stringify({ text, targetLang })
        }),

    // Folder management
    createFolder: (name: string, parentId: string | null, token: string | undefined) =>
        fetchJson<Folder>("/api/folders", token, {
            method: "POST",
            body: JSON.stringify({ name, parentId })
        }),
    deleteFolder: async (folderId: string, token: string | undefined) => {
        const headers = new Headers();
        if (token) headers.set("Authorization", `Bearer ${token}`);
        const res = await fetch(new URL(`/api/folders/${encodeURIComponent(folderId)}`, baseUrl).toString(), {
            method: "DELETE",
            headers
        });
        if (!res.ok) {
            const body = await res.text().catch(() => res.statusText);
            throw new Error(`Delete folder → ${res.status}: ${body}`);
        }
    },
    markAllRead: (folderId: string, token: string | undefined) =>
        fetchJson<{ count: number }>(
            `/api/folders/${encodeURIComponent(folderId)}/mark-all-read`,
            token,
            { method: "POST" }
        ),
    emptyTrash: (token: string | undefined) =>
        fetchJson<{ count: number }>("/api/folders/trash/empty", token, { method: "POST" })
};

export interface SearchParams {
    query?: string;
    from?: string;
    to?: string;
    subject?: string;
    folder?: string;
    hasAttachments?: boolean;
    isFlagged?: boolean;
    isUnread?: boolean;
    category?: string;
    after?: string;
    before?: string;
    take?: number;
    skip?: number;
}

export interface SearchResult {
    totalHits: number;
    messages: MessageSummary[];
    skip?: number;
    take?: number;
}

export interface RelocateResult {
    /** The message's id in its new folder; null when the server couldn't report it. */
    id: string | null;
    folderId: string;
}

// AI Chat
export interface AiChatMessage { role: "user" | "assistant"; content: string; }
export interface AiEmailContext {
    subject: string;
    fromName: string;
    fromEmail: string;
    /** Plain-text body (HTML stripped by frontend, ≤3 000 chars) */
    bodyText: string;
}
export interface AiChatRequest {
    mode: "filter" | "compose" | "reply" | "general" | "translate" | "rewrite" | "signature" | "autoreply" | "summarize";
    messages: AiChatMessage[];
    emailContext?: AiEmailContext;
    /** When true, the caller has a saved signature and will append it, so the AI
     *  should NOT invent its own sign-off/signature. */
    hasSignature?: boolean;
}
export interface AiFilterSuggestion { name: string; conditionsJson: string; actionsJson: string; }
export interface AiComposeSuggestion { subject?: string; bodyHtml?: string; to?: string | null; cc?: string | null; bcc?: string | null; }
export interface AiChatResponse {
    reply: string;
    generatedFilter?: AiFilterSuggestion;
    composeSuggestion?: AiComposeSuggestion;
}

export interface CalendarSettings {
    /** Add invites and bookings found in email to the calendar (default on). */
    eventsFromEmail: boolean;
    /** ISO weekdays, 1 = Monday … 7 = Sunday. */
    workDays: number[];
    /** "HH:mm" in {@link timeZone}. */
    workStart: string;
    workEnd: string;
    /** IANA zone the calendar shows times in (default Asia/Kolkata). */
    timeZone: string;
}

/** GET/PUT /api/mail/preferences. `version` 0 = nothing saved for this user yet. */
export interface PreferencesEnvelope {
    version: number;
    prefs: unknown;
    updatedAt?: string | null;
}

/** Thrown for a non-2xx Mail BFF response; `status` lets the query client retry 503s quietly. */
export class MailApiError extends Error {
    readonly status: number;
    /** What the server actually said, for the console and support — never shown to the user. */
    readonly technical: string;
    constructor(message: string, status: number, technical?: string) {
        super(message);
        this.name = "MailApiError";
        this.status = status;
        this.technical = technical ?? message;
    }

    /**
     * The message a person can act on. A problem response's `detail` is written for people (the
     * BFF phrases it so); anything else — a proxy's HTML page, a stack trace, a provider's JSON —
     * is replaced by a plain sentence, and the raw text is kept on `technical` and logged.
     */
    static from(path: string, status: number, body: string): MailApiError {
        const technical = `Mail BFF ${path} → ${status}: ${body}`;
        let message: string | null = null;
        try {
            const p = JSON.parse(body) as { title?: unknown; detail?: unknown };
            const detail = typeof p.detail === "string" ? p.detail.trim() : "";
            const title = typeof p.title === "string" ? p.title.trim() : "";
            // A detail that is itself a machine payload is not a sentence.
            if (detail && !/^[\[{]|^\d{3}:\s*[\[{]/.test(detail)) message = detail;
            else if (title && !detail) message = title;
        } catch {
            /* not JSON */
        }
        if (!message) {
            message = status === 401 || status === 403
                ? "Your session has expired or you don't have access. Sign in again and retry."
                : status >= 500
                    ? "The mail service didn't respond as expected. Try again in a moment."
                    : "The request couldn't be completed. Check what you entered and try again.";
        }
        console.warn(technical);
        return new MailApiError(message, status, technical);
    }
}

export interface SafeSender {
    id: string;
    /** Lower-case address ("name@example.com") or whole domain ("@example.com"). */
    address: string;
    createdAt: string;
}

export interface TranslateSegmentsResponse {
    /** Same length and order as the request; untranslated pieces come back as sent. */
    segments: string[];
    partial: boolean;
    provider: string;
}

export interface TranslateResponse {
    translatedText: string;
    detectedSourceLang: string | null;
    provider: string;
}

async function requireNoContent(
    path: string,
    token: string | undefined,
    method: "POST" | "DELETE",
    body?: unknown
): Promise<void> {
    const headers = new Headers();
    if (token) headers.set("Authorization", `Bearer ${token}`);
    if (body !== undefined) headers.set("Content-Type", "application/json");
    const res = await fetch(new URL(path, baseUrl).toString(), {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body)
    });
    if (!res.ok) throw new Error(`Mail BFF ${path} → ${res.status} ${res.statusText}`);
}
